import { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useStaffAuth } from '@/contexts/StaffAuthContext';
import { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { loadRemembered, saveRemembered } from '@/hooks/useRememberedLogin';
import { Eye, EyeOff } from 'lucide-react';

const REMEMBER_KEY = 'tiffin_remembered_staff_login';

export default function StaffLogin() {
  const { login, staff } = useStaffAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const saved = loadRemembered(REMEMBER_KEY);
    if (saved) {
      setEmail(saved.email);
      setPassword(saved.password);
      setRemember(true);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
      saveRemembered(REMEMBER_KEY, email, password, remember);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (staff) return <Navigate to="/admin/dashboard" replace />;

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img
            src="/logo-emblem.png"
            alt="The Tiffin by The Virsa"
            className="mx-auto w-24 h-24 rounded-full object-cover shadow-2xl ring-1 ring-white/10"
          />
          <p className="text-white/50 text-sm mt-4">Staff &amp; Management Console</p>
        </div>
        <form onSubmit={submit} className="bg-[#151515] border border-white/10 rounded-2xl p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-white/70">Email</Label>
            <Input
              id="email"
              type="email"
              required
              data-testid="staff-login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white/5 border-white/15 text-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-white/70">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                data-testid="staff-login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/5 border-white/15 text-white pr-10"
              />
              <button
                type="button"
                data-testid="staff-login-toggle-password-visibility"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors duration-150"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer select-none">
            <Checkbox
              data-testid="staff-login-remember-me-checkbox"
              checked={remember}
              onCheckedChange={(v) => setRemember(!!v)}
              className="border-white/30"
            />
            Remember Me
          </label>

          {error && (
            <p data-testid="staff-login-error" className="text-sm text-red-400">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="staff-login-submit-btn"
            className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover transition-colors duration-150"
          >
            {busy ? 'Signing in…' : 'Log In'}
          </Button>
        </form>
        <p className="text-center mt-5">
          <a href="/portal/login" data-testid="console-to-portal-link" className="text-xs text-white/40 hover:text-white/70 underline">
            Are you staff? Go to the Staff Portal →
          </a>
        </p>
      </div>
    </div>
  );
}
